import { Component, Input, OnChanges } from '@angular/core';
import Highcharts from 'highcharts';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, chartColors } from '../report-aggregation';
import { SLICE_CENTRE, SLICE_SIZE_PX, SLICE_SPACING, sliceLegend, slicePoints, withDeckMotion } from './chart-layout';

@Component({
  selector: 'app-pie-chart-block',
  standalone: false,
  template: `
    @if (chartOptions) {
      <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="chartOptions"
                        style="width: 100%; display: block;"></highcharts-chart>
    } @else if (multiResponse) {
      <p class="grey4 nav-font12 mb-0">
        This question accepts more than one answer, so its options sum past 100% and cannot be slices
        of one pie. Chart it as a Donut instead.
      </p>
    }
  `
})
export class PieChartBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;

  highcharts = Highcharts;
  chartOptions: Highcharts.Options | null = null;
  multiResponse = false;

  ngOnChanges() {
    const series = this.series[0];
    const points = series?.points ?? [];

    // A pie of a multi-punch question either contradicts its own slice sizes or quietly switches to
    // "share of all selections", which every reader hears as "share of people". Saying so beats
    // drawing it. The donut block renders these as one small donut per option.
    this.multiResponse = !!series?.multiResponse;
    if (this.multiResponse) {
      this.chartOptions = null;
      return;
    }

    const legendRows = Math.max(points.length, this.alignCount ?? 0);
    // One per slice: a nine-option question used to give options 6-9 the colours of options 1-4.
    const roles = chartColors(this.palette, Math.max(1, points.length));
    const hideLabels = !!(this.block.reportExtraData as { hideLabels?: boolean }).hideLabels;

    this.chartOptions = withDeckMotion({
      chart: {
        type: 'pie',
        // Sized by the legend, which is the tall side now: the pie is a fixed 110px beside it.
        height: Math.max(150, legendRows * 18 + 40),
        spacing: SLICE_SPACING,
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: { pointFormat: '<b>{point.y:.0f}%</b> ({point.custom.count})' },
      // A pie's answer text lives in its legend, so that is what the setting hides here.
      legend: hideLabels ? { enabled: false } : sliceLegend(!!this.compact),
      plotOptions: {
        pie: {
          // Flat px rather than a share of the chart, and the figures live in the legend -- see
          // the donut block, which this matches deliberately: the two are the same chart with and
          // without a hole, so they should not read as two different layouts.
          size: SLICE_SIZE_PX,
          center: SLICE_CENTRE,
          showInLegend: true,
          borderWidth: 0,
          // Square slices: Highcharts rounds every slice by default, which puts a curve at each
          // join between two answers rather than only where the ring begins and ends.
          borderRadius: 0,
          dataLabels: { enabled: false },
        },
      },
      series: [{
        type: 'pie',
        name: series?.name ?? '',
        data: slicePoints(points, roles),
      }],
    });
  }
}
